const getApiBase = () => {
  const configured = (process.env.REACT_APP_API_URL || '').trim();
  if (configured) {
    try {
      const parsed = new URL(
        configured,
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
      );
      const pathname = parsed.pathname.replace(/\/+$/, '');
      const basePath = pathname.replace(/\/api$/, '');

      return {
        origin: parsed.origin,
        basePath,
      };
    } catch (_) {
      // Fall through to window origin.
    }
  }

  if (typeof window !== 'undefined') {
    return {
      origin: window.location.origin,
      basePath: '',
    };
  }

  return {
    origin: '',
    basePath: '',
  };
};

const normalizePath = (path) => {
  if (typeof path !== 'string') return '';
  return path
    .trim()
    .replace(/^\/+/, '')
    .replace(/^storage\/+/, '');
};

const isLocalHostname = (hostname) =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';

const appendIfMissing = (collector, value) => {
  if (!value) return;
  if (!collector.includes(value)) {
    collector.push(value);
  }
};

const resolveMediaApiPath = (rawUrl) => {
  if (!rawUrl) return '';

  try {
    const parsed = new URL(
      rawUrl,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    const marker = '/api/media/';
    const markerIndex = parsed.pathname.indexOf(marker);
    if (markerIndex >= 0) {
      return parsed.pathname.slice(markerIndex);
    }
  } catch (_) {
    // Ignore malformed URLs and continue.
  }

  return '';
};

const buildStorageUrl = ({ origin, basePath }, cleanPath) => {
  const normalizedBasePath = (basePath || '').replace(/\/+$/, '');

  if (!origin) {
    return `${normalizedBasePath}/storage/${cleanPath}`.replace(/\/{2,}/g, '/');
  }

  return `${origin.replace(/\/+$/, '')}${normalizedBasePath}/storage/${cleanPath}`;
};

const resolvePathFromRawUrl = (rawUrl) => {
  if (!rawUrl) return '';

  try {
    const parsed = new URL(
      rawUrl,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    const marker = '/storage/';
    const markerIndex = parsed.pathname.indexOf(marker);
    if (markerIndex >= 0) {
      return parsed.pathname.slice(markerIndex + marker.length);
    }
  } catch (_) {
    // Ignore malformed URLs and continue.
  }

  return '';
};

const buildApiUrl = ({ origin, basePath }, id) => {
  if (!id && id !== 0) return '';
  const normalizedBasePath = (basePath || '').replace(/\/+$/, '');

  if (!origin) {
    return `${normalizedBasePath}/api/media/${id}`.replace(/\/{2,}/g, '/');
  }

  return `${origin.replace(/\/+$/, '')}${normalizedBasePath}/api/media/${id}`;
};

export const resolveMediaAssetUrlCandidates = (asset) => {
  const candidates = [];
  const rawUrl = typeof asset?.url === 'string' ? asset.url.trim() : '';
  const rawApiUrl = typeof asset?.api_url === 'string' ? asset.api_url.trim() : '';
  const rawPath = typeof asset?.path === 'string' ? asset.path : '';
  const numericId = Number(asset?.id);
  const inferredPath = resolvePathFromRawUrl(rawUrl);
  const inferredApiPath = resolveMediaApiPath(rawApiUrl || rawUrl);
  const cleanPath = normalizePath(rawPath || inferredPath);
  const apiBase = getApiBase();

  if (rawApiUrl) {
    appendIfMissing(candidates, rawApiUrl);
  }

  if (inferredApiPath) {
    if (apiBase.origin) {
      appendIfMissing(candidates, `${apiBase.origin}${inferredApiPath}`);
    }
    appendIfMissing(candidates, inferredApiPath);
  }

  if (Number.isFinite(numericId) && numericId > 0) {
    appendIfMissing(candidates, buildApiUrl(apiBase, numericId));

    if (apiBase.origin) {
      appendIfMissing(candidates, buildApiUrl({ origin: apiBase.origin, basePath: '' }, numericId));
      appendIfMissing(candidates, buildApiUrl({ origin: apiBase.origin, basePath: '/app' }, numericId));
    }

    appendIfMissing(candidates, buildApiUrl({ origin: '', basePath: '' }, numericId));
    appendIfMissing(candidates, buildApiUrl({ origin: '', basePath: '/app' }, numericId));
  }

  if (rawUrl) {
    try {
      const parsed = new URL(
        rawUrl,
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
      );

      if (!isLocalHostname(parsed.hostname) || typeof window === 'undefined') {
        appendIfMissing(candidates, parsed.toString());
      }
    } catch (_) {
      appendIfMissing(candidates, rawUrl);
    }
  }

  if (!cleanPath) {
    return candidates;
  }

  appendIfMissing(candidates, buildStorageUrl(apiBase, cleanPath));

  if (apiBase.origin) {
    appendIfMissing(
      candidates,
      buildStorageUrl({ origin: apiBase.origin, basePath: '' }, cleanPath)
    );
    appendIfMissing(
      candidates,
      buildStorageUrl({ origin: apiBase.origin, basePath: '/app' }, cleanPath)
    );
  }

  appendIfMissing(candidates, buildStorageUrl({ origin: '', basePath: '' }, cleanPath));
  appendIfMissing(candidates, buildStorageUrl({ origin: '', basePath: '/app' }, cleanPath));

  return candidates;
};

export const resolveMediaAssetUrl = (asset) => resolveMediaAssetUrlCandidates(asset)[0] || '';
